import usePrivateLink from "../config/usePrivateLink";
import React, { useEffect, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import {
  formatDate,
  ToastMain,
  SweetAlertShow,
  LoadingDiv,
} from "../master/Master";
import Swal from "sweetalert2";
import {
  noticeList,
  noticeDetails,
  addNotice,
  updateNotice,
  updateNoticeStatus,
  deleteNotice,
} from "../../api/NoticeService";

import { studentsListOnly } from "../../api/Student";
import { StaffList } from "../../api/Staff";
import { getBatchList } from "../../api/BatchService";
import AcademicHeader from "./AcademicHeader";

const APPLICABLE_OPTIONS = [
  { value: "all_student", label: "All Students" },
  { value: "all_staff", label: "All Staff" },
  { value: "both", label: "All Students & Staff" },
  { value: "students", label: "Specific Students" },
  { value: "staffs", label: "Specific Staff" },
  { value: "batches", label: "Specific Batch" },
];

const applicableBadge = (value) => {
  const found = APPLICABLE_OPTIONS.find((o) => o.value === value);
  return (
    <span className="badge bg-label-info rounded-pill px-3">
      {found?.label || value}
    </span>
  );
};

const emptyForm = {
  id: null,
  title: "",
  description: "",
  applicable: "all_student",
  student_ids: [],
  staff_ids: [],
  batch_ids: [],
};

// Resolve stored ids into display names using an already-loaded list
const resolveNames = (ids, list, labelKey = "name") =>
  (ids || [])
    .map((id) => (list || []).find((x) => x.id === id)?.[labelKey])
    .filter(Boolean);

// ── Reusable searchable multi-select checkbox picker ──────────────────────
const MultiSelectPicker = ({
  options,
  selectedIds,
  onChange,
  labelKey = "name",
  subLabelKey,
  placeholder = "Search...",
}) => {
  const [search, setSearch] = useState("");

  const filtered = (options || []).filter((o) => {
    const q = search.toLowerCase();
    return (
      (o[labelKey] || "").toLowerCase().includes(q) ||
      (subLabelKey && (o[subLabelKey] || "").toLowerCase().includes(q))
    );
  });

  const toggle = (id) => {
    const numId = Number(id);
    if (selectedIds.includes(numId)) {
      onChange(selectedIds.filter((x) => x !== numId));
    } else {
      onChange([...selectedIds, numId]);
    }
  };

  return (
    <div>
      <input
        type="text"
        className="form-control form-control-sm mb-2"
        placeholder={placeholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div
        className="border rounded-3 p-2"
        style={{ maxHeight: 220, overflowY: "auto" }}
      >
        {filtered.length > 0 ? (
          filtered.map((opt) => (
            <div className="form-check py-1" key={opt.id}>
              <input
                type="checkbox"
                className="form-check-input"
                id={`picker_${labelKey}_${opt.id}`}
                checked={selectedIds.includes(opt.id)}
                onChange={() => toggle(opt.id)}
              />
              <label
                className="form-check-label d-flex justify-content-between w-100"
                htmlFor={`picker_${labelKey}_${opt.id}`}
              >
                <span>{opt[labelKey]}</span>
                {subLabelKey && opt[subLabelKey] && (
                  <small className="text-muted">{opt[subLabelKey]}</small>
                )}
              </label>
            </div>
          ))
        ) : (
          <small className="text-muted">No results.</small>
        )}
      </div>
      {selectedIds.length > 0 && (
        <small className="text-muted d-block mt-1">
          {selectedIds.length} selected
        </small>
      )}
    </div>
  );
};

export default function Notices() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [notices, setNotices] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isEdit, setIsEdit] = useState(false);

  const [studentsList, setStudentsList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [batchList, setBatchList] = useState([]);

  // ── View details modal state ──
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    getNotices();
  }, [selectedType, search]);

  useEffect(() => {
    getStudentsForPicker();
    getStaffForPicker();
    getBatchesForPicker();
  }, []);

  const getNotices = async () => {
    const addUrl = `&search=${search || ""}&status=${selectedType}`;
    setLoading(true);
    try {
      const response = await noticeList(addUrl);

      if (response?.data?.success) {
        setNotices(response?.data?.data?.data || response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getStudentsForPicker = async () => {
    try {
      const response = await studentsListOnly("");
      if (response?.data?.status) {
        setStudentsList(
          response?.data?.data?.data || response?.data?.data || [],
        );
      }
    } catch (error) {
      // silent — picker just stays empty, main page isn't blocked
    }
  };

  const getStaffForPicker = async () => {
    try {
      const response = await StaffList("");
      if (response?.data?.status) {
        setStaffList(response?.data?.data?.data || response?.data?.data || []);
      }
    } catch (error) {
      // silent
    }
  };

  const getBatchesForPicker = async () => {
    try {
      const response = await getBatchList("");
      if (response?.data?.status) {
        setBatchList(response?.data?.data?.data || response?.data?.data || []);
      }
    } catch (error) {
      // silent
    }
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setErrors({});
    setIsEdit(false);
    setEditLoading(false);
  };

  const openEditModal = async (item) => {
    setErrors({});
    setIsEdit(true);
    setForm({ ...emptyForm, id: item.id });
    setEditLoading(true);

    try {
      const response = await noticeDetails(item.id);
      if (response?.data?.success) {
        const notice = response.data.data;
        setForm({
          id: notice.id,
          title: notice.title || "",
          description: notice.description || "",
          applicable: notice.applicable || "all_student",
          student_ids: (notice.student_ids || []).map(Number),
          staff_ids: (notice.staff_ids || []).map(Number),
          batch_ids: (notice.batch_ids || []).map(Number),
        });
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setEditLoading(false);
    }
  };

  const handleViewDetails = async (item) => {
    setViewData(null);
    setViewLoading(true);
    try {
      const response = await noticeDetails(item.id);
      if (response?.data?.success) {
        setViewData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setViewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!form.title.trim()) {
      setErrors({ title: "Title is required." });
      return;
    }

    const payload = {
      title: form.title,
      description: form.description,
      applicable: form.applicable,
      student_ids: form.applicable === "students" ? form.student_ids : [],
      staff_ids: form.applicable === "staffs" ? form.staff_ids : [],
      batch_ids: form.applicable === "batches" ? form.batch_ids : [],
    };

    setLoading(true);
    try {
      const response = isEdit
        ? await updateNotice({ id: form.id, ...payload })
        : await addNotice(payload);

      if (response?.data?.success) {
        document.getElementById("notice_form_close")?.click();
        ToastMain.fire({
          icon: "success",
          title: response?.data?.message || "Success",
        }).then(() => {
          getNotices();
        });
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      ToastMain.fire({
        icon: "error",
        title: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const newStatus = item.status == "1" ? "2" : "1";
    const ok = await Swal.fire({
      title: newStatus === "2" ? "Mark Inactive?" : "Mark Active?",
      html: `Are you sure you want to mark <b>${item.title}</b> as ${newStatus === "2" ? "inactive" : "active"
        }?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });
    if (!ok?.isConfirmed) return;

    setLoading(true);
    try {
      const res = await updateNoticeStatus({ id: item.id, status: newStatus });
      if (res?.data?.success) {
        ToastMain.fire({
          icon: "success",
          title: res?.data?.message || "Status updated.",
        });
        getNotices();
      } else {
        SweetAlertShow("info", "Warning", res?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    const ok = await Swal.fire({
      title: "Delete Notice?",
      html: `Are you sure you want to delete <b>${item.title}</b>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    });
    if (!ok?.isConfirmed) return;

    setLoading(true);
    try {
      const res = await deleteNotice(item.id);
      if (res?.data?.success) {
        ToastMain.fire({
          icon: "success",
          title: res?.data?.message || "Notice deleted.",
        });
        getNotices();
      } else {
        SweetAlertShow("info", "Warning", res?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid mt-4">
                <AcademicHeader />
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body">
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-4">
                      <div>
                        <h3 className="mb-1 fw-bold text-primary">
                          <i className="ri-notification-3-line me-2"></i>
                          Notice Board
                        </h3>
                        <p className="text-muted mb-0">
                          Manage notices for students and staff.
                        </p>
                      </div>
                      {hasAccess("/notice/add") && (
                        <button
                          type="button"
                          className="btn btn-primary mt-3 mt-lg-0"
                          data-bs-toggle="modal"
                          data-bs-target="#notice_form_modal"
                          onClick={openAddModal}
                        >
                          <i className="ri-add-line me-1"></i>
                          Add Notice
                        </button>
                      )}
                    </div>

                    <div className="row align-items-end">
                      <div className="col-lg-5">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search Notice"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                          <label>Search by Title</label>
                        </div>
                      </div>
                      <div className="col-lg-7 mt-3 mt-lg-0">
                        <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                          <button
                            className={`btn rounded-pill px-4 ${selectedType == ""
                                ? "btn-primary"
                                : "btn-outline-primary"
                              }`}
                            onClick={() => setSelectedType("")}
                          >
                            All
                          </button>
                          <button
                            className={`btn rounded-pill px-4 ${selectedType == "1"
                                ? "btn-success"
                                : "btn-outline-success"
                              }`}
                            onClick={() => setSelectedType("1")}
                          >
                            Active
                          </button>
                          <button
                            className={`btn rounded-pill px-4 ${selectedType == "2"
                                ? "btn-danger"
                                : "btn-outline-danger"
                              }`}
                            onClick={() => setSelectedType("2")}
                          >
                            Inactive
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table align-middle table-hover">
                        <thead className="table-light">
                          <tr>
                            <th width="60">#</th>
                            <th>Title</th>
                            <th width="180">Applicable To</th>
                            <th width="150">Created</th>
                            <th width="120">Status</th>
                            <th width="230" className="text-center">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {notices?.length > 0 ? (
                            notices.map((item, i) => (
                              <tr key={item.id}>
                                <td className="fw-semibold text-muted">
                                  {i + 1}
                                </td>
                                <td>
                                  <div className="fw-semibold">
                                    {item.title}
                                  </div>
                                  <small
                                    className="text-muted text-truncate d-block"
                                    style={{ maxWidth: 320 }}
                                  >
                                    {item.description}
                                  </small>
                                </td>
                                <td>{applicableBadge(item.applicable)}</td>
                                <td>
                                  <i className="ri-calendar-line text-success me-2"></i>
                                  {formatDate(item.created_at)}
                                </td>
                                <td>
                                  {hasAccess("/notice/status/update") && (
                                    <span
                                      role="button"
                                      className={`badge rounded-pill px-3 py-2 ${item.status == "1"
                                          ? "bg-label-success"
                                          : "bg-label-danger"
                                        }`}
                                      onClick={() => handleToggleStatus(item)}
                                    >
                                      <i
                                        className={`me-1 ${item.status == "1"
                                            ? "ri-checkbox-circle-fill"
                                            : "ri-close-circle-fill"
                                          }`}
                                      ></i>
                                      {item.status == "1"
                                        ? "Active"
                                        : "Inactive"}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex justify-content-center gap-2">
                                    {hasAccess("/notice/view") && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        title="View Notice"
                                        data-bs-toggle="modal"
                                        data-bs-target="#notice_view_modal"
                                        onClick={() => handleViewDetails(item)}
                                      >
                                        <i className="ri-eye-line"></i>
                                      </button>
                                    )}
                                    {hasAccess("/notice/update") && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-warning"
                                        title="Edit Notice"
                                        data-bs-toggle="modal"
                                        data-bs-target="#notice_form_modal"
                                        onClick={() => openEditModal(item)}
                                      >
                                        <i className="ri-pencil-line"></i>
                                      </button>
                                    )}
                                    {hasAccess("/notice/delete") && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        title="Delete Notice"
                                        onClick={() => handleDelete(item)}
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6">
                                <div className="py-5 text-center">
                                  <div
                                    className="rounded-circle bg-label-secondary d-inline-flex align-items-center justify-content-center mb-3"
                                    style={{ width: 80, height: 80 }}
                                  >
                                    <i
                                      className="ri-notification-3-line"
                                      style={{ fontSize: 34 }}
                                    ></i>
                                  </div>
                                  <h5 className="mb-1">No Notices Found</h5>
                                  <p className="text-muted mb-0">
                                    Try changing your search or filters.
                                  </p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════ ADD / EDIT NOTICE MODAL ══════════════════ */}
      <div
        className="modal fade"
        id="notice_form_modal"
        tabIndex={-1}
        aria-labelledby="noticeFormLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header py-3 bg-label-primary">
                <h1 className="modal-title fs-5" id="noticeFormLabel">
                  {isEdit ? "Update Notice" : "Add Notice"}
                </h1>
                <button
                  type="button"
                  id="notice_form_close"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                {isEdit && editLoading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                    />
                    <p className="text-muted small mt-2 mb-0">
                      Loading notice details...
                    </p>
                  </div>
                ) : (
                  <div className="row g-3">
                    <div className="col-md-12">
                      <div className="form-floating form-floating-outline">
                        <input
                          type="text"
                          className={`form-control ${errors.title ? "is-invalid" : ""}`}
                          placeholder="Title"
                          value={form.title}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, title: e.target.value }))
                          }
                        />
                        <label>Title</label>
                        {errors.title && (
                          <div className="invalid-feedback">
                            {Array.isArray(errors.title)
                              ? errors.title[0]
                              : errors.title}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-floating form-floating-outline">
                        <textarea
                          className="form-control"
                          placeholder="Description"
                          style={{ height: 110 }}
                          value={form.description}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              description: e.target.value,
                            }))
                          }
                        />
                        <label>Description</label>
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-floating form-floating-outline">
                        <select
                          className="form-select"
                          value={form.applicable}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              applicable: e.target.value,
                            }))
                          }
                        >
                          {APPLICABLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <label>Applicable To</label>
                      </div>
                    </div>

                    {form.applicable === "students" && (
                      <div className="col-md-12">
                        <label className="form-label fw-semibold small mb-1">
                          Select Students
                        </label>
                        <MultiSelectPicker
                          options={studentsList}
                          selectedIds={form.student_ids}
                          onChange={(ids) =>
                            setForm((p) => ({ ...p, student_ids: ids }))
                          }
                          labelKey="name"
                          subLabelKey="registration_no"
                          placeholder="Search students by name or reg. no..."
                        />
                      </div>
                    )}

                    {form.applicable === "staffs" && (
                      <div className="col-md-12">
                        <label className="form-label fw-semibold small mb-1">
                          Select Staff
                        </label>
                        <MultiSelectPicker
                          options={staffList}
                          selectedIds={form.staff_ids}
                          onChange={(ids) =>
                            setForm((p) => ({ ...p, staff_ids: ids }))
                          }
                          labelKey="name"
                          subLabelKey="registration_no"
                          placeholder="Search staff by name or reg. no..."
                        />
                      </div>
                    )}

                    {form.applicable === "batches" && (
                      <div className="col-md-12">
                        <label className="form-label fw-semibold small mb-1">
                          Select Batch
                        </label>
                        <MultiSelectPicker
                          options={batchList}
                          selectedIds={form.batch_ids}
                          onChange={(ids) =>
                            setForm((p) => ({ ...p, batch_ids: ids }))
                          }
                          labelKey="title"
                          placeholder="Search batches by name..."
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer py-3 bg-label-primary">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isEdit && editLoading}
                >
                  {isEdit ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ══════════════════ VIEW NOTICE DETAILS MODAL ══════════════════ */}
      <div
        className="modal fade"
        id="notice_view_modal"
        tabIndex={-1}
        aria-labelledby="noticeViewLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header py-3 bg-label-primary">
              <h1 className="modal-title fs-5" id="noticeViewLabel">
                Notice Details
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              {viewLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : viewData ? (
                <div className="row g-3">
                  <div className="col-12">
                    <small className="text-muted d-block">Title</small>
                    <div className="fw-semibold fs-5">{viewData.title}</div>
                  </div>
                  <div className="col-12">
                    <small className="text-muted d-block">Description</small>
                    <div>{viewData.description || "—"}</div>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted d-block">Applicable To</small>
                    {applicableBadge(viewData.applicable)}
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted d-block">Status</small>
                    <span
                      className={`badge rounded-pill px-3 py-2 ${viewData.status == "1"
                          ? "bg-label-success"
                          : "bg-label-danger"
                        }`}
                    >
                      {viewData.status == "1" ? "Active" : "Inactive"}
                    </span>
                  </div>

                  {viewData.applicable === "students" && (
                    <div className="col-12">
                      <small className="text-muted d-block mb-1">
                        Students
                      </small>
                      {viewData.student_names?.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {viewData.student_names.map((s) => (
                            <span
                              key={s.id}
                              className="badge bg-label-secondary"
                            >
                              {s.name}{" "}
                              {s.registration_no
                                ? `(${s.registration_no})`
                                : ""}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <small className="text-muted">—</small>
                      )}
                    </div>
                  )}

                  {viewData.applicable === "staffs" && (
                    <div className="col-12">
                      <small className="text-muted d-block mb-1">Staff</small>
                      {viewData.staff_names?.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {viewData.staff_names.map((s) => (
                            <span
                              key={s.id}
                              className="badge bg-label-secondary"
                            >
                              {s.name}{" "}
                              {s.registration_no
                                ? `(${s.registration_no})`
                                : ""}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <small className="text-muted">—</small>
                      )}
                    </div>
                  )}

                  {viewData.applicable === "batches" && (
                    <div className="col-12">
                      <small className="text-muted d-block mb-1">Batches</small>
                      {viewData.batch_names?.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {viewData.batch_names.map((b) => (
                            <span
                              key={b.id}
                              className="badge bg-label-secondary"
                            >
                              {b.title}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <small className="text-muted">—</small>
                      )}
                    </div>
                  )}

                  <div className="col-md-6">
                    <small className="text-muted d-block">Created</small>
                    <div>{formatDate(viewData.created_at)}</div>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted d-block">Last Updated</small>
                    <div>
                      {viewData.updated_at
                        ? formatDate(viewData.updated_at)
                        : "—"}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  No data found.
                </div>
              )}
            </div>
            <div className="modal-footer py-3">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
